// src/settings/AnnouncementPage.jsx
'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Box,
} from '@mui/material';
import PremiumInput from '../common/components/premium/PremiumInput';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MessageIcon from '@mui/icons-material/Message';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import SelectField from '../common/components/SelectField';
import { prefixString } from '../common/util/stringUtils';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import PremiumTabs, { PremiumTabPanel } from '../common/components/PremiumTabs';

const AnnouncementPage = () => {
  const navigate = useNavigate();
  const { classes } = useSettingsStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Form state
  const [message, setMessage] = useState({ subject: '', body: '' });
  const [notificator, setNotificator] = useState();
  const [sendAll, setSendAll] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Users state
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Lazy-load users
  const loadUsers = async () => {
    if (usersLoaded || sendAll) return;
    setLoadingUsers(true);
    try {
      const res = await fetchOrThrow('/api/users?excludeAttributes=true');
      const data = await res.json();
      const normalized = data.map((u) => ({
        id: u.id,
        name: u.name || u.email || String(u.id),
        email: u.email,
      }));
      normalized.sort((a, b) => a.name.localeCompare(b.name));
      setUsers(normalized);
      setUsersLoaded(true);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSend = async () => {
    if (!notificator || !message.subject || !message.body) return;

    const query = new URLSearchParams();
    if (!sendAll) {
      selectedUsers.forEach((u) => query.append('userId', u.id));
    }

    const payload = {
      subject: message.subject.trim(),
      body: message.body.trim(),
    };

    await fetchOrThrow(
      `/api/notifications/send/${notificator}?${query.toString()}${sendAll ? '&all=true' : ''}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );

    navigate(-1);
  };

  const canSend =
    !!notificator &&
    !!message.subject?.trim() &&
    !!message.body?.trim() &&
    (sendAll || selectedUsers.length > 0);

  const checkboxSx = {
    color: tokens.border.default,
    '&.Mui-checked': { color: tokens.accent.primary },
  };

  const tabs = [
    { icon: <NotificationsIcon />, label: t('sharedRequired') },
    { icon: <MessageIcon />, label: t('commandMessage') },
  ];

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['serverAnnouncement']}>
      <Container maxWidth={false} className={classes.container}>

        <PremiumTabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          tabs={tabs}
        />

        {/* Tab 0: Geral (Notificator + Users) */}
        <PremiumTabPanel value={activeTab} index={0}>
          <Box display="flex" flexDirection="column" gap={2} width="100%">
            {/* Notificator */}
            <Box
              display="flex"
              width="100%"
              gap={2}
              sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
            >
              <Box flex={1} minWidth={0}>
                <SelectField
                  fullWidth
                  value={notificator}
                  onChange={(e) => setNotificator(e.target.value)}
                  endpoint="/api/notifications/notificators?announcement=true"
                  keyGetter={(it) => it.type}
                  titleGetter={(it) => t(prefixString('notificator', it.type))}
                  label={t('notificationNotificators')}
                />
              </Box>
            </Box>

            {/* Users */}
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendAll}
                    onChange={(e) => setSendAll(e.target.checked)}
                    sx={checkboxSx}
                  />
                }
                label={t('announcementSendAll') || 'Enviar para todos os usuários'}
              />
              <Box sx={{ width: '100%', opacity: sendAll ? 0.5 : 1 }}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  disabled={sendAll}
                  options={users}
                  loading={loadingUsers}
                  value={selectedUsers}
                  onChange={(_, v) => setSelectedUsers(v)}
                  onOpen={loadUsers}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <Box component="li" key={option.id} {...rest}>
                        {option.name}
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('settingsUsers')} placeholder="Selecione..." className="premium-input" />
                  )}
                />
              </Box>
            </Box>
          </Box>
        </PremiumTabPanel>

        {/* Tab 1: Mensagem */}
        <PremiumTabPanel value={activeTab} index={1}>
          <Box display="flex" flexDirection="column" gap={2} width="100%">
            {/* Subject */}
            <Box
              display="flex"
              width="100%"
              gap={2}
              sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
            >
              <Box flex={1} minWidth={0}>
                <PremiumInput
                  value={message.subject}
                  onChange={(e) => setMessage({ ...message, subject: e.target.value })}
                  label={t('sharedSubject')}
                />
              </Box>
            </Box>

            {/* Message body */}
            <Box width="100%">
              <PremiumInput
                value={message.body}
                onChange={(e) => setMessage({ ...message, body: e.target.value })}
                label={t('commandMessage')}
                multiline
                rows={4}
              />
            </Box>
          </Box>
        </PremiumTabPanel>

        {/* Buttons */}
        <div className={classes.buttons}>
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: tokens.radius.md,
              borderColor: tokens.border.default,
              '&:hover': {
                borderColor: tokens.accent.primary,
              },
            }}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSend}
            disabled={!canSend}
            sx={{
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.accent.primary,
              '&:hover': {
                backgroundColor: tokens.accent.hover,
              },
            }}
          >
            {t('commandSend')}
          </Button>
        </div>

      </Container>
    </PageLayout>
  );
};

export default AnnouncementPage;
